/**
 * Funnel event tracking
 *
 * Records what a visitor does between arriving on Diil and finishing an interview, so
 * drop-off between steps can be measured. The event list and payload contract are in
 * FUNNEL_TRACKING.md.
 *
 * Every event is sent to two places:
 *   1. GA4 (gtag)
 *   2. The OnePlaceHR internal dashboard's public intake, POST /api/diil-events
 *
 * Like the contact/feedback forms (see api/services/feedback.js), this goes
 * straight to the dashboard rather than through hrSystemServer - a plain
 * cross-origin fetch that cannot introduce an error into Diil's own backends,
 * and needs no work from the backend team.
 *
 * Tracking must never break the user experience: nothing in here can throw.
 * These calls sit inside click handlers ("Чатлах", "CV илгээх"), so an exception
 * escaping from one would stop the button it was measuring.
 */

import { detectPlatform } from '../api/services/jobView';
import { getTrackedSource } from './sourceDetection';
import {
  getViewSessionId,
  getVisitorId,
  randomId,
  sessionGetJson,
  sessionSetJson,
} from './trackingSession';

const EVENTS_URL =
  process.env.REACT_APP_DIIL_EVENTS_URL ||
  'https://internal.diil.work/api/diil-events';

// Event names. GA4 reserves some names (session_start, first_visit, page_view,
// user_engagement, error …) — none of these may be renamed to one of those.
export const EVENTS = {
  // Visit. visit_start: once per session, as soon as the app loads, whatever the landing
  // page. visit_end: each time the page is hidden or closed (a session can have several;
  // the last one is the exit). route_view: every in-app navigation after the landing.
  VISIT_START: 'visit_start',
  VISIT_END: 'visit_end',
  ROUTE_VIEW: 'route_view',
  // Discovery
  // job_view: the job page itself, once per session per job, next to recordJobView().
  // This is the only step a visitor reaches without clicking anything - without it, a
  // campaign that brings visits but no clicks looks identical to a campaign nobody saw.
  JOB_VIEW: 'job_view',
  // job_preview: a job shown in full somewhere other than its page (map sheet, story,
  // preview drawer) — places with their own CV/Чатлах buttons. meta.surface says where.
  JOB_PREVIEW: 'job_preview',
  JOB_SEARCH: 'job_search',
  // Apply
  INTERVIEW_CLICK: 'interview_click',
  CV_SEND_CLICK: 'cv_send_click',
  CV_SEND_SUCCESS: 'cv_send_success',
  CV_SEND_FAILED: 'cv_send_failed',
  CV_INCOMPLETE: 'cv_incomplete',
  // Auth
  LOGIN_PROMPT_SHOWN: 'login_prompt_shown',
  LOGIN_OTP_SENT: 'login_otp_sent',
  LOGIN_SUCCESS: 'login_success',
  LOGIN_FAILED: 'login_failed',
  // A login that created the account (isNewUser) — sent alongside login_success
  SIGN_UP: 'sign_up',
  // Interview chat
  CHAT_STARTED: 'chat_started',
  CHAT_LOAD_FAILED: 'chat_load_failed',
  CHAT_FIRST_MESSAGE: 'chat_first_message',
  // Every message the candidate sends (text, voice, image) with its position, so how far
  // someone got never depends on an unload event surviving.
  CHAT_MESSAGE: 'chat_message',
  CHAT_SEND_FAILED: 'chat_send_failed',
  // aichatbot answered with conversationEnded: the interview is finished.
  CHAT_COMPLETED: 'chat_completed',
  // Left the chat page before it finished. meta.userMessages = messages sent this visit.
  CHAT_ABANDONED: 'chat_abandoned',
  // Errors
  APP_ERROR: 'app_error',
};

// Kept out of GA4: high-volume events GA already covers (page_view, engagement time) or
// that would only add noise there. They still reach the dashboard.
const DASHBOARD_ONLY = new Set([EVENTS.ROUTE_VIEW, EVENTS.VISIT_END, EVENTS.CHAT_MESSAGE]);

// --- Identity --------------------------------------------------------------------------

let currentCandidateId = null;

/**
 * Who is signed in, for every event that follows. Called by TrackingRoot whenever the
 * user changes; null on logout.
 */
export const setTrackingUser = (candidateId) => {
  const next = candidateId != null && candidateId !== '' ? Number(candidateId) : null;
  const normalized = Number.isFinite(next) ? next : null;
  if (normalized === currentCandidateId) return;
  currentCandidateId = normalized;
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('set', { user_id: normalized != null ? String(normalized) : null });
    }
  } catch (error) {
    // GA blocked
  }
};

export const getCurrentPath = () => {
  try {
    return window.location.pathname;
  } catch (error) {
    return null;
  }
};

// --- Payload -----------------------------------------------------------------------------

// Drop null/undefined so the payload stays small and the store gets clean rows
const compact = (obj) => {
  const out = {};
  Object.keys(obj).forEach((key) => {
    if (obj[key] !== null && obj[key] !== undefined && obj[key] !== '') {
      out[key] = obj[key];
    }
  });
  return out;
};

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const buildPayload = (eventName, context = {}) => {
  const tracked = getTrackedSource();
  // Identity and location ride in meta: the top-level fields are the intake's existing
  // columns, and meta is the free-form JSON it already stores — so this needs no change
  // on the dashboard to be kept.
  const meta = compact({
    ...(context.meta || {}),
    path: context.meta?.path ?? getCurrentPath(),
    visitorId: getVisitorId(),
    candidateId: currentCandidateId,
    eventId: randomId(),
  });
  return compact({
    eventName,
    assessmentId: toNumberOrNull(context.assessmentId),
    companyId: toNumberOrNull(context.companyId),
    platform: detectPlatform(),
    sessionId: getViewSessionId(),
    sourceChannel: tracked?.source,
    // Which post/story inside that channel. Only ever set from utm_medium /
    // utm_campaign / utm_content on the landing URL.
    sourceMedium: tracked?.medium,
    sourceCampaign: tracked?.campaign,
    sourceContent: tracked?.content,
    occurredAt: context.occurredAt || new Date().toISOString(),
    // Free-form extras (error reason, message count, identity, etc.)
    meta: JSON.stringify(meta),
  });
};

// --- GA4 -----------------------------------------------------------------------------------

// meta keys worth having as GA4 event parameters. GA4 caps a string parameter at 100
// characters; anything longer is cut, not dropped.
const GA_META_KEYS = [
  'from', 'trigger', 'reason', 'surface', 'step', 'method', 'type', 'entry', 'kind',
  'results', 'query', 'userMessages', 'messageCount', 'isNewUser', 'partTime',
  'landingPath', 'referrerHost', 'inApp', 'device', 'visitNumber', 'chunk',
];

const toSnake = (key) => (key === 'query' ? 'search_term' : key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`));

const gaValue = (value) => {
  if (Array.isArray(value)) return value.join(',').slice(0, 100);
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'number') return value;
  if (value == null) return undefined;
  return String(value).slice(0, 100);
};

const sendToGa = (eventName, payload, meta) => {
  try {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    const params = compact({
      assessment_id: payload.assessmentId,
      company_id: payload.companyId,
      platform: payload.platform,
      source_channel: payload.sourceChannel,
      source_medium: payload.sourceMedium,
      source_campaign: payload.sourceCampaign,
      session_id: payload.sessionId,
    });
    GA_META_KEYS.forEach((key) => {
      if (meta && meta[key] !== undefined) {
        const value = gaValue(meta[key]);
        if (value !== undefined && value !== '') params[toSnake(key)] = value;
      }
    });
    window.gtag('event', eventName, params);
  } catch (error) {
    // GA blocked by an ad blocker - not worth surfacing
  }
};

// --- Dashboard transport -------------------------------------------------------------------
//
// text/plain is a CORS-safelisted content type, so the request goes out with no preflight:
// half the requests, and keepalive/sendBeacon are reliable with it (neither can be relied on
// to preflight). The intake parses the raw body as JSON whatever the content type.
//
// Should that ever stop being true, a 4xx on a text/plain send is retried once as
// application/json, and if that works every later event goes as JSON straight away. If the
// JSON send fails too, the content type was not the problem (the intake refused that event
// itself, e.g. a name it does not know yet) — that event name is not retried again on this
// page, so a refused event costs one request, not two, while every other name still gets
// its retry.

const TEXT_TYPE = 'text/plain;charset=UTF-8';
let preferJson = false;
const refusedNames = new Set();

const postJson = (json, keepalive) =>
  fetch(EVENTS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: json,
    keepalive,
  });

const send = (eventName, json, { unload = false } = {}) => {
  if (unload && !preferJson) {
    try {
      // sendBeacon survives the page being closed; a normal fetch would be cancelled.
      const blob = new Blob([json], { type: TEXT_TYPE });
      if (navigator.sendBeacon && navigator.sendBeacon(EVENTS_URL, blob)) return;
    } catch (error) {
      // fall through to keepalive fetch below
    }
  }

  try {
    if (preferJson) {
      postJson(json, true).catch(() => {});
      return;
    }
    fetch(EVENTS_URL, {
      method: 'POST',
      headers: { 'Content-Type': TEXT_TYPE },
      body: json,
      keepalive: true,
    })
      .then((response) => {
        if (response.ok || unload || refusedNames.has(eventName)) return;
        if (response.status < 400 || response.status >= 500 || response.status === 429) return;
        postJson(json, false)
          .then((retry) => {
            if (retry.ok) preferJson = true;
            else refusedNames.add(eventName);
          })
          .catch(() => {});
      })
      .catch(() => {
        // network error or blocked - stay silent so the console isn't spammed
      });
  } catch (error) {
    // ignore
  }
};

// --- Public API ----------------------------------------------------------------------------

/**
 * Record a funnel event. Fire-and-forget - never await this in a UI path.
 * @param {string} eventName - One of EVENTS
 * @param {Object} context - { assessmentId, companyId, meta, occurredAt }
 * @param {Object} [options] - { unload: page is going away, use sendBeacon }
 */
export const trackEvent = (eventName, context = {}, options = {}) => {
  try {
    const payload = buildPayload(eventName, context);
    if (!DASHBOARD_ONLY.has(eventName)) {
      sendToGa(eventName, payload, context.meta);
    }
    send(eventName, JSON.stringify(payload), { unload: !!options.unload });
  } catch (error) {
    // never let analytics reach the UI
  }
};

/**
 * Record an event during page unload. Uses sendBeacon so the request survives
 * the page being closed - a normal fetch would be cancelled.
 * @param {string} eventName - One of EVENTS
 * @param {Object} context - { assessmentId, companyId, meta }
 */
export const trackEventOnUnload = (eventName, context = {}) =>
  trackEvent(eventName, context, { unload: true });

// --- Once-per-session helpers ----------------------------------------------------------------
//
// hrSystemServer dedupes /job-view/record by session, but the dashboard's event store keeps
// every row it is sent, so de-duplication for views has to happen here — JobDetail remounts
// on back-navigation and on every :id change. Keyed by session id, so a new session (new
// campaign arrival) starts clean.

const ONCE_KEY = 'diil_tracked_once';
const ONCE_LIMIT = 500;

const readOnce = () => {
  const sessionId = getViewSessionId();
  const stored = sessionGetJson(ONCE_KEY, null);
  const keys = stored && stored.sid === sessionId && Array.isArray(stored.keys) ? stored.keys : [];
  return { sessionId, keys };
};

/** True the first time `key` is seen this session, false after. Never throws. */
export const firstTimeThisSession = (key) => {
  try {
    const { sessionId, keys } = readOnce();
    if (keys.includes(key)) return false;
    keys.push(key);
    if (keys.length > ONCE_LIMIT) keys.splice(0, keys.length - ONCE_LIMIT);
    sessionSetJson(ONCE_KEY, { sid: sessionId, keys });
    return true;
  } catch (error) {
    // record rather than lose it; a rare duplicate beats a missing funnel step
    return true;
  }
};

/** How many distinct jobs this session has had a job_view for. */
export const countJobsViewedThisSession = () => {
  try {
    return readOnce().keys.filter((key) => key.startsWith('job_view:') && key !== 'job_view:0').length;
  } catch (error) {
    return 0;
  }
};

/**
 * Record a job page view, at most once per session per job.
 * assessmentId 0 = a landing on the home feed rather than one job.
 * @param {number} assessmentId
 * @param {number} [companyId]
 */
export const trackJobView = (assessmentId, companyId) => {
  if (assessmentId == null) return;
  const id = Number(assessmentId);
  if (!Number.isFinite(id)) return;
  if (!firstTimeThisSession(`job_view:${id}`)) return;
  trackEvent(EVENTS.JOB_VIEW, { assessmentId: id, companyId });
};

/**
 * Record a job shown somewhere other than its own page, once per session per job per
 * surface ("map_sheet", "story", "drawer").
 */
export const trackJobPreview = (assessmentId, companyId, surface) => {
  if (assessmentId == null) return;
  const id = Number(assessmentId);
  if (!Number.isFinite(id) || id <= 0) return;
  if (!firstTimeThisSession(`job_preview:${surface}:${id}`)) return;
  trackEvent(EVENTS.JOB_PREVIEW, { assessmentId: id, companyId, meta: { surface } });
};

const trackingService = { EVENTS, trackEvent, trackEventOnUnload };

export default trackingService;
